import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

const WISHLIST_MARKERS = ['wishlist', 'wish-list', 'favorite', 'favourite'];

/**
 * @param {Element} element
 * @returns {boolean}
 */
function looksLikeWishlistControl(element) {
  const attributes = Array.from(element.attributes, ({ name, value }) => `${name}=${value}`).join(' ');
  const interactiveText = element.matches('a, button, [role="button"]') ? element.textContent || '' : '';
  const descriptor = `${element.localName} ${attributes} ${interactiveText}`.toLowerCase();

  return WISHLIST_MARKERS.some((marker) => descriptor.includes(marker));
}

/**
 * @param {Element} match
 * @param {HTMLElement} actions
 * @returns {Element | null}
 */
function getWishlistActionWrapper(match, actions) {
  let node = match.closest('a, button, [role="button"]') || match;

  while (node.parentElement && node.parentElement !== actions) {
    const parent = node.parentElement;

    if (parent.matches('.header__column--right, #header-component, header-actions')) break;
    if (parent.matches('.header-menu, .menu-drawer, .search-action, .account-button')) break;

    node = parent;
  }

  return node;
}

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  /** @type {MutationObserver | null} */
  #wishlistObserver = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#syncCartTriggerAriaExpanded();
    this.#setupWishlistPositioning();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#wishlistObserver?.disconnect();
    this.#wishlistObserver = null;
  }

  #setupWishlistPositioning = () => {
    const header = this.closest('#header-component');
    if (!header) return;

    this.#positionWishlistAction(header);

    this.#wishlistObserver?.disconnect();
    this.#wishlistObserver = new MutationObserver(() => this.#positionWishlistAction(header));
    this.#wishlistObserver.observe(header, { childList: true, subtree: true });
  };

  /**
   * Keeps Wishlist by Square's own header control between Account and Cart.
   * The app-owned node is moved rather than recreated so its events, count,
   * accessibility state, and future app updates remain intact.
   * @param {Element} header
   */
  #positionWishlistAction = (header) => {
    const cartAction = this.querySelector('[data-testid="cart-drawer-trigger"], .action__cart');
    if (!cartAction) return;

    const candidates = Array.from(header.querySelectorAll('*'));
    const match = candidates.find((element) => {
      if (element === this || element === cartAction) return false;
      if (element.closest('.header-menu, .menu-drawer, gw-header-predictive-search')) return false;
      if (element.closest('.account-button')) return false;
      if (element.matches('[data-testid="cart-drawer-trigger"], .action__cart')) return false;

      return looksLikeWishlistControl(element);
    });

    if (!match) {
      this.classList.remove('gw-header-actions--has-wishlist');
      return;
    }

    const wishlistAction = getWishlistActionWrapper(match, this);
    if (!wishlistAction || wishlistAction === cartAction || wishlistAction.contains(this)) return;

    wishlistAction.classList.add('gw-header-wishlist-action');
    this.classList.add('gw-header-actions--has-wishlist');

    if (wishlistAction.parentElement === this && wishlistAction.nextElementSibling === cartAction) return;

    this.insertBefore(wishlistAction, cartAction);
  };

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartTriggerAriaExpanded();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const cartCount = cart?.totalQuantity;
        if (cartCount === undefined) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
